import { useState } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAuth, PERMISSIONS } from '../contexts/AuthContext';
import './UserManagement.css';
import { Button } from '@/components/ui/button';

const TOKEN_STORAGE_KEY = 'aipiracy_session_token';

const PERMISSION_LABELS: Record<string, { label: string; description: string }> = {
  [PERMISSIONS.VIEW]: {
    label: 'View',
    description: 'View scan results and reports',
  },
  [PERMISSIONS.SCAN]: {
    label: 'Scan',
    description: 'Trigger new scans',
  },
  [PERMISSIONS.ANALYTICS]: {
    label: 'Analytics',
    description: 'Access analytics and reports',
  },
  [PERMISSIONS.AUTOMATION]: {
    label: 'Automation',
    description: 'Set up scheduled scans',
  },
  [PERMISSIONS.ADMIN]: {
    label: 'Admin',
    description: 'User management and system settings',
  },
};

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);

  // Get admin token
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);

  // Fetch users
  const users = useQuery(api.auth.listUsers, token ? { adminToken: token } : "skip");

  // Mutations & Actions
  const createUser = useMutation(api.auth.createUser);
  const updateUser = useMutation(api.auth.updateUser);
  const deleteUser = useMutation(api.auth.deleteUser);
  const sendWelcomeEmail = useAction(api.auth.sendWelcomeEmail);

  const handleCreateUser = async (formData: {
    email: string;
    name: string;
    permissions: string[];
  }) => {
    if (!token) return;

    try {
      const result = await createUser({
        adminToken: token,
        email: formData.email,
        name: formData.name,
        permissions: formData.permissions,
      });

      // Always send welcome email with auto-generated password
      try {
        await sendWelcomeEmail({
          email: formData.email,
          name: formData.name,
          password: result.password,
        });
        alert('✅ User created successfully and welcome email sent with temporary password');
      } catch (emailError: any) {
        alert(`⚠️ User created but email failed: ${emailError.message}\n\nTemporary password: ${result.password}\n\nPlease share this password securely with the user.`);
      }

      setShowAddUser(false);
    } catch (error: any) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleUpdateUser = async (userId: string, updates: any) => {
    if (!token) return;

    try {
      await updateUser({
        adminToken: token,
        userId: userId as any,
        ...updates,
      });
      setEditingUser(null);
      alert('✅ User updated successfully');
    } catch (error: any) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!token) return;

    const confirmed = window.confirm(
      `⚠️ Delete user "${userName}"?\n\nThis will:\n` +
      `• Delete the user account\n` +
      `• Revoke all active sessions\n\n` +
      `This action CANNOT be undone.`
    );

    if (!confirmed) return;

    try {
      await deleteUser({
        adminToken: token,
        userId: userId as any,
      });
      alert('✅ User deleted successfully');
    } catch (error: any) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  if (!users) {
    return <div>Loading users...</div>;
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <Button onClick={() => setShowAddUser(true)}>+ Add User</Button>
      </div>

      <div className="users-list">
        {users.map((user) => (
          <div key={user.id} className="user-card">
            <div className="user-info">
              <div className="user-header">
                <div>
                  <h4>{user.name}</h4>
                  <span className="user-email">{user.email}</span>
                </div>
                <div className="user-badges">
                  {!user.active && (
                    <span className="badge badge-inactive">Inactive</span>
                  )}
                  {user.id === currentUser?.id && (
                    <span className="badge badge-you">You</span>
                  )}
                </div>
              </div>

              <div className="user-permissions">
                {user.permissions.map((perm) => (
                  <span key={perm} className="permission-badge">
                    {PERMISSION_LABELS[perm]?.label || perm}
                  </span>
                ))}
              </div>

              <div className="user-meta">
                {user.lastLoginAt && (
                  <span>
                    Last login: {new Date(user.lastLoginAt).toLocaleString()}
                  </span>
                )}
                <span>
                  Created: {new Date(user.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="user-actions">
              <Button variant="outline" size="sm" onClick={() => setEditingUser(user)}>Edit</Button>
              {user.id !== currentUser?.id && (
                <Button variant="destructive" size="sm" onClick={() => handleDeleteUser(user.id, user.name)}>Delete</Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showAddUser && (
        <UserFormModal
          title="Add New User"
          onClose={() => setShowAddUser(false)}
          onSubmit={handleCreateUser}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSubmit={(updates) => handleUpdateUser(editingUser.id, updates)}
        />
      )}
    </div>
  );
}

function UserFormModal({
  title,
  onClose,
  onSubmit,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (data: any) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<string[]>([PERMISSIONS.VIEW]);

  const togglePermission = (perm: string) => {
    setPermissions((prev) =>
      prev.includes(perm)
        ? prev.filter((p) => p !== perm)
        : [...prev, perm]
    );
  };

  const handleSubmit = () => {
    if (!email || !name) {
      alert('Please fill all required fields');
      return;
    }
    onSubmit({ email, name, permissions });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>

        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
          />
        </div>

        <div className="form-group">
          <label>Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
          />
        </div>

        <div className="form-group">
          <label>Permissions</label>
          <div className="permissions-grid">
            {Object.entries(PERMISSION_LABELS).map(([key, { label, description }]) => (
              <label key={key} className="permission-checkbox">
                <input
                  type="checkbox"
                  checked={permissions.includes(key)}
                  onChange={() => togglePermission(key)}
                />
                <div>
                  <strong>{label}</strong>
                  <span>{description}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div style={{
          background: '#f0f9ff',
          border: '1px solid #bfdbfe',
          borderRadius: '6px',
          padding: '0.875rem',
          marginTop: '1rem'
        }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af', fontWeight: '500' }}>
            ℹ️ A secure temporary password will be automatically generated and sent to the user's email.
            The user will be required to change this password upon first login.
          </p>
        </div>

        <div className="modal-actions">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Create User</Button>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({
  user,
  onClose,
  onSubmit,
}: {
  user: any;
  onClose: () => void;
  onSubmit: (updates: any) => void;
}) {
  const [name, setName] = useState(user.name);
  const [permissions, setPermissions] = useState<string[]>(user.permissions);
  const [active, setActive] = useState(user.active);
  const [newPassword, setNewPassword] = useState('');

  const togglePermission = (perm: string) => {
    setPermissions((prev) =>
      prev.includes(perm)
        ? prev.filter((p) => p !== perm)
        : [...prev, perm]
    );
  };

  const handleSubmit = () => {
    const updates: any = {
      name,
      permissions,
      active,
    };
    if (newPassword) {
      updates.newPassword = newPassword;
    }
    onSubmit(updates);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Edit User</h3>

        <div className="form-group">
          <label>Email (read-only)</label>
          <input type="email" value={user.email} disabled />
        </div>

        <div className="form-group">
          <label>Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>New Password (leave blank to keep current)</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="••••••••"
          />
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            <span>Account Active</span>
          </label>
        </div>

        <div className="form-group">
          <label>Permissions</label>
          <div className="permissions-grid">
            {Object.entries(PERMISSION_LABELS).map(([key, { label, description }]) => (
              <label key={key} className="permission-checkbox">
                <input
                  type="checkbox"
                  checked={permissions.includes(key)}
                  onChange={() => togglePermission(key)}
                />
                <div>
                  <strong>{label}</strong>
                  <span>{description}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="modal-actions">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Save Changes</Button>
        </div>
      </div>
    </div>
  );
}
